import { useState, useRef, useEffect } from "react";
import dynamic from "next/dynamic";
import MainContainer from "components/sections/MainContainer";
import MetaHead from "components/base/MetaHead";
import HomeHeroHeader from "components/sections/HomeHeroHeader";
import QuoteSection from "components/sections/QuoteSection";
import { allowedLocales } from "translations";
import { useOnScreen } from "utils/hooks";
import { getRecentPosts, getRecentEpisodes } from "lib/payloadApi";
import SectionErrorBoundary from "components/base/SectionErrorBoundary";

const HomeVideoSection = dynamic(
  () => import("components/sections/HomeVideoSection"),
);

const HomeVideoSection2 = dynamic(
  () => import("components/sections/VideoSection"),
);

const TargetDatesSection = dynamic(
  () => import("components/sections/TargetDatesSection"),
);
const ThumbnailLinksSection = dynamic(
  () => import("components/sections/ThumbnailLinksSection"),
);
const BlogSection = dynamic(() => import("components/sections/BlogSection"));
const FloatingCTA = dynamic(() => import("components/blocks/FloatingCTA"));
const Trans = dynamic(() => import("components/base/Trans"));
const TextColumnSection = dynamic(
  () => import("components/sections/TextColumnSection"),
);
const SeenOn = dynamic(() => import("components/sections/SeenOn"));
const TestimonialsSection = dynamic(
  () => import("components/sections/TestimonialsSection"),
);
const SalesReportSection = dynamic(
  () => import("components/sections/SalesReportSection"),
);

const homepageSchema = JSON.stringify({
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": "https://www.hillhelicopters.com/#organization",
      name: "Hill Helicopters",
      url: "https://www.hillhelicopters.com",
      logo: "https://www.hillhelicopters.com/images/favicon-256.png",
      sameAs: [
        "https://www.instagram.com/hillhelicopters",
        "https://www.facebook.com/hillhelicopters",
        "https://www.youtube.com/hillhelicopters",
      ],
    },
    {
      "@type": "WebSite",
      "@id": "https://www.hillhelicopters.com/#website",
      url: "https://www.hillhelicopters.com",
      name: "Hill Helicopters",
      publisher: { "@id": "https://www.hillhelicopters.com/#organization" },
      potentialAction: {
        "@type": "SearchAction",
        target: "https://www.hillhelicopters.com/faq?id={search_term_string}",
        "query-input": "required name=search_term_string",
      },
    },
    {
      "@type": "Service",
      "@id": "https://www.hillhelicopters.com/#service",
      name: "HX50 Helicopter Pre-Order",
      description:
        "Pre-order the HX50, a revolutionary 5-seat turbine helicopter designed for owner-pilots.",
      provider: { "@id": "https://www.hillhelicopters.com/#organization" },
      areaServed: "Worldwide",
      serviceType: "Helicopter Sales",
      url: "https://www.hillhelicopters.com",
    },
  ],
});

const Home = ({ recentEpisodes, recentPosts }) => {
  const [loaded, setLoaded] = useState(false);
  const preBlogSectionRef = useRef();
  const heroHeaderSectionRef = useRef();

  const showingHeroHeader = useOnScreen(heroHeaderSectionRef, "-20%");
  const [isLastSection, setLastSection] = useState(false);
  const showCTA = !isLastSection && !showingHeroHeader;

  useEffect(() => {
    // This is a legitimate one-time mount effect
    setLoaded(true); // eslint-disable-line
  }, []);

  return (
    <MainContainer
      navStyle={{ gradient: true, inverted: false }}
      updateVisible={setLastSection}
    >
      <MetaHead title="LBL_HOME_META_TITLE" structuredData={homepageSchema} />
  
      <SectionErrorBoundary name="HomeHeroHeader">
        <div ref={heroHeaderSectionRef}>
          <HomeHeroHeader
            animationId="homeHeader2b"
            imInterestedClick={() => {
              document
                .getElementById("register")
                .scrollIntoView({ behavior: "smooth" });
            }}
          />
        </div>
      </SectionErrorBoundary>
      <SectionErrorBoundary name="QuoteSection">
        <QuoteSection
          animationId="quote2"
          quote={<Trans id="LBL_HOME_QUOTE" />}
          author={<Trans id="LBL_HOME_AUTHOR" />}
          title={<Trans id="LBL_HOME_TITLE" />}
        />
      </SectionErrorBoundary>
      <div ref={preBlogSectionRef} />
      {loaded && (
        <>
          <SectionErrorBoundary name="HomeVideoSection2">
            <HomeVideoSection2
              subtitle="LBL_HOME_VIDEO_SECTION2_SUBTITLE"
              title="LBL_HOME_VIDEO_SECTION2_TITLE"
              ctaLabel="LBL_HOME_VIDEO_SECTION2_CTA"
              thumbnailImage={{ base: "/images/home/thumb_engine.jpg" }}
              thumbnailObjectPosition="center"
              videoId="3J5OSsgVglY"
              backgroundVideo={{
                videoSrcDesktop:
                  "https://stream.mux.com/cJpKivNYZwIONW7ikLVLV4a5WUIXZfZ6n5ji4C00g5CM.m3u8",
                videoSrcMobile:
                  "https://stream.mux.com/cJpKivNYZwIONW7ikLVLV4a5WUIXZfZ6n5ji4C00g5CM.m3u8",
                fallbackImage: {
                  base: "/images/home/video-section-mobile2.jpg",
                  md: "/images/home/video-section2.jpg",
                },
              }}
            />
          </SectionErrorBoundary>
          <SectionErrorBoundary name="TargetDatesSection">
            <TargetDatesSection />
          </SectionErrorBoundary>
          <SectionErrorBoundary name="HomeVideoSection">
            <HomeVideoSection
              image={{
                base: "/images/home/video-section-mobile2.jpg",
                md: "/images/home/video-section2.jpg",
              }}
              videoId="O_GUFDR52so"
            />
          </SectionErrorBoundary>
          <SectionErrorBoundary name="ThumbnailLinksSection">
            <ThumbnailLinksSection />
          </SectionErrorBoundary>
          <SectionErrorBoundary name="TextColumnSection">
            <TextColumnSection
              title={<Trans id="LBL_HOME_COLUMN_TITLE" />}
              subtitle={<Trans id="LBL_HOME_COLUMN_SUBTITLE_1" />}
              subtitle2={<Trans id="LBL_HOME_COLUMN_SUBTITLE_2" />}
              text={<Trans id="LBL_HOME_COLUMN_TEXT" />}
            />
          </SectionErrorBoundary>
          <SectionErrorBoundary name="SeenOn">
            <SeenOn />
          </SectionErrorBoundary>
          <SectionErrorBoundary name="TestimonialsSection">
            <TestimonialsSection
              quotes={[
                <Trans key="quote1" id="LBL_HOME_TESTIMONIAL_QUOTE_1" />,
                <Trans key="quote2" id="LBL_HOME_TESTIMONIAL_QUOTE_2" />,
              ]}
              authors={[
                <Trans key="author1" id="LBL_HOME_TESTIMONIAL_AUTHOR_1" />,
                <Trans key="author2" id="LBL_HOME_TESTIMONIAL_AUTHOR_2" />,
              ]}
              titles={[
                <Trans key="title1" id="LBL_HOME_TESTIMONIAL_TITLE_1" />,
                <Trans key="title2" id="LBL_HOME_TESTIMONIAL_TITLE_2" />,
              ]}
              videos={["qSJY0FgqXYk", "pkpkeA0Lb_s"]}
            />
          </SectionErrorBoundary>
          <SectionErrorBoundary name="BlogSection">
            <BlogSection
              recentPosts={recentPosts}
              recentEpisodes={recentEpisodes}
              invert
            />
          </SectionErrorBoundary>
          <SectionErrorBoundary name="SalesReportSection">
            <SalesReportSection />
          </SectionErrorBoundary>
          <SectionErrorBoundary name="FloatingCTA">
            <FloatingCTA show={showCTA} />
          </SectionErrorBoundary>
        </>
      )}
    </MainContainer>
  );
};

export default function Index({ recentPosts, recentEpisodes }) {
  return (
    <Home
      recentPosts={recentPosts}
      recentEpisodes={recentEpisodes}
    />
  );
}

export async function getStaticProps({ locale }) {
  const selectedLocale =
    allowedLocales.indexOf(locale) > -1 ? locale : null;

  // fetch both in parallel and avoid crashing if one or both fail
  const [postsResult, episodesResult] = await Promise.allSettled([
    getRecentPosts(selectedLocale),
    getRecentEpisodes(selectedLocale),
  ]);

  const recentPosts =
    postsResult.status === "fulfilled" ? postsResult.value : [];
  if (postsResult.status === "rejected") {
    console.error("### getRecentPosts failed:", postsResult.reason);
  }

  const recentEpisodes =
    episodesResult.status === "fulfilled" ? episodesResult.value : [];
  if (episodesResult.status === "rejected") {
    console.error("getRecentEpisodes failed:", episodesResult.reason);
  }

  return {
    props: {
      recentPosts,
      recentEpisodes,
    },
    revalidate: 60,
  };
}

